import { Editor, Element, Node, NodeEntry, Path, Transforms } from 'slate';
import { getBlockTagBindings, isBlockTagCarrierType, TagBinding } from '../types/block';
import { TagElement } from '../types';
import { isTag } from './navigation';

/**
 * Locate the block-tag carrier whose `tags[]` binding holds `tagId`, regardless
 * of tag metadata. Returns null when the tag is not a block binding (e.g. an
 * inline prose tag, or absent). Lets delete flows detect block tags by id so a
 * by-id delete can never remove the DB row while leaving the Slate binding.
 */
export function findBlockCarrierForTag(editor: Editor, tagId: string): NodeEntry | null {
  const entry = Array.from(
    Editor.nodes(editor, {
      at: [],
      match: (n) => Element.isElement(n) && isBlockTagCarrierType((n as { type?: string }).type) && getBlockTagBindings(n).some((b) => b.tagId === tagId),
    })
  )[0];
  return entry ?? null;
}

export function applyBlockTagBinding(editor: Editor, path: Path, binding: TagBinding): void {
  const node = Node.get(editor, path);
  const existing = getBlockTagBindings(node);
  const next = [...existing, binding];
  Transforms.setNodes(editor, { tags: next } as Partial<Node>, { at: path });
}

/**
 * Remove a block-tag binding from the carrier at `path` and repair the
 * continuation chain: any carrier whose binding pointed at `tagId` is
 * re-stitched to the removed binding's successor (or has its `continuedAt`
 * cleared if the removed binding was the tail). Mirrors the inline `removeTag`
 * relink and the backend `nextSegment` repair so the Slate chain never dangles.
 */
export function removeBlockTagBinding(editor: Editor, path: Path, tagId: string): void {
  const node = Node.get(editor, path);
  const removed = getBlockTagBindings(node).find((b) => b.tagId === tagId);
  const successorId = removed?.continuedAt;

  relinkPredecessor(editor, tagId, successorId);

  writeBlockBindings(
    editor,
    path,
    getBlockTagBindings(node).filter((b) => b.tagId !== tagId)
  );
}

export function writeBlockBindings(editor: Editor, path: Path, bindings: TagBinding[]): void {
  if (bindings.length === 0) Transforms.unsetNodes(editor, 'tags', { at: path });
  else Transforms.setNodes(editor, { tags: bindings } as Partial<Node>, { at: path });
}

/** Re-point (or clear) `continuedAt` on whichever inline tag or block binding continues into `tagId`. */
export function relinkPredecessor(editor: Editor, tagId: string, successorId: string | undefined): void {
  const predecessors = Array.from(
    Editor.nodes(editor, {
      at: [],
      match: (n) => (isTag(n) && n.continuedAt === tagId) || getBlockTagBindings(n).some((b) => b.continuedAt === tagId),
    })
  );
  for (const [predNode, predPath] of predecessors) {
    if (isTag(predNode)) {
      Transforms.setNodes(editor, { continuedAt: successorId || undefined } as Partial<TagElement>, { at: predPath });
      continue;
    }
    const relinked = getBlockTagBindings(predNode).map((b) => {
      if (b.continuedAt !== tagId) return b;
      if (successorId) return { ...b, continuedAt: successorId };
      const { continuedAt: _continuedAt, ...rest } = b;
      return rest;
    });
    Transforms.setNodes(editor, { tags: relinked } as Partial<Node>, { at: predPath });
  }
}
